import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Field, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import Asterisk from '@atoms/Asterisk';
import { Button, FormInput, FormLabel, Label, ErrorText } from '@styled';

// Functionality
import { FormikInput } from '../../elements/FieldTypes';
import { getCountriesState, getStatesState } from '../../../reducers/FoundationDonationReducer';

const FormColumn = styled.div`
    width: 100%;
    @media (min-width: 768px) {
        width: 50%;
    }
`;

const DonorInfo = ({
    // Passed
    member,
    company,
    selectedCountry,
    onCompanyChange,
    onCountryChange,
    onMemberSave,

    // Redux
    countryList,
    stateProvinceList,
}) => {
    // -----------------------------
    // State
    // -----------------------------

    const formik = useFormikContext();

    const [statesByCountry, setStatesByCountry] = useState(
        stateProvinceList.filter(state => state.country === selectedCountry)
    );

    // -----------------------------
    // Effects
    // -----------------------------

    useEffect(() => {
        setStatesByCountry(stateProvinceList.filter(state => state.country === selectedCountry));
    }, [stateProvinceList, selectedCountry]);

    // -----------------------------
    // Render
    // -----------------------------

    return (
        <>
            <fieldset className="mb-3">
                <FormColumn className="align-top d-inline-block">
                    <FormikInput name="firstName" type="text" label="First Name" required />
                    <FormikInput name="middleName" type="text" label="Middle Name" />
                    <FormikInput name="lastName" type="text" label="Last Name" required />
                    <FormikInput name="email" type="text" label="Email" required disabled={!!member} />

                    <Label className="mb-3 mb-md-2" htmlFor="companyID">
                        <FormLabel>
                            Company
                            <Asterisk />
                        </FormLabel>

                        <FormInput name="company" type="text" required value={company} onChange={onCompanyChange} />
                    </Label>
                </FormColumn>

                <FormColumn className="align-top d-inline-block">
                    <FormikInput name="address" type="text" label="Address" required />
                    <FormikInput name="city" type="text" label="City" required />
                    <Label className="mb-3 mb-md-2">
                        <FormLabel>
                            {formik.touched.stateProvince && formik.errors.stateProvince && (
                                <ErrorText>{formik.errors.stateProvince}</ErrorText>
                            )}
                            {(!formik.touched.stateProvince || !formik.errors.stateProvince) && (
                                <>
                                    State/Province
                                    <Asterisk />
                                </>
                            )}
                        </FormLabel>
                        <Field as="select" name="stateProvince" style={{ width: '250px' }}>
                            <option>Select...</option>
                            {statesByCountry.map(state => (
                                <option value={state.code} key={state.code}>
                                    {state.name}
                                </option>
                            ))}
                        </Field>
                    </Label>

                    <FormikInput name="zip" type="text" label="Zip/Mail Code" required />

                    <Label className="mb-3 mb-md-2">
                        <FormLabel>
                            Country
                            <Asterisk />
                        </FormLabel>

                        <Field
                            as="select"
                            name="country"
                            style={{ width: '250px' }}
                            onChange={event => {
                                onCountryChange(event);
                            }}
                        >
                            {(countryList || []).map(country => (
                                <option value={country.code} key={country.name}>
                                    {country.name}
                                </option>
                            ))}
                        </Field>
                    </Label>
                </FormColumn>
            </fieldset>

            {member && (
                <Button
                    type="button"
                    className="small-button"
                    onClick={() => {
                        onMemberSave();
                    }}
                >
                    Update Billing Information
                </Button>
            )}
        </>
    );
};

DonorInfo.propTypes = {
    // Passed
    member: PropTypes.object,
    company: PropTypes.string,
    selectedCountry: PropTypes.string.isRequired,
    onCompanyChange: PropTypes.func.isRequired,
    onCountryChange: PropTypes.func.isRequired,
    onMemberSave: PropTypes.func.isRequired,

    // Redux
    countryList: PropTypes.array.isRequired,
    stateProvinceList: PropTypes.array,
};

// -----------------------------
// Connect Redux
// -----------------------------

const mapStateToProps = state => ({
    countryList: getCountriesState(state),
    stateProvinceList: getStatesState(state),
});

export default connect(mapStateToProps)(DonorInfo);
